import { FaMoon, FaSun, FaUserCog, FaFolderOpen, FaLock, FaSignOutAlt, FaUserShield, FaBuilding, FaIdBadge, FaAngleDown, FaAngleRight, FaBars, FaBell, FaCheckDouble, FaCalendarAlt, FaTasks, FaBullhorn, FaInfoCircle } from "react-icons/fa";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Breadcrumbs from "../homepage/breadcrumbs";
import { useAuth } from "../context/authcontext";
import { updateProfile } from "../services/profileservice";
import LogoutModal from "../profile/logout";
// NOTE: adjust this import path to match where your axios instance
// actually lives relative to this file (see org-chart's "../services/api").
import api from "../services/api";

// ── Notification API endpoints — point these at your real backend routes. ──
const NOTIF_LIST_URL = "notifications/";
const NOTIF_UNREAD_COUNT_URL = "notifications/unread-count/";
const NOTIF_MARK_READ_URL = (id) => `notifications/${id}/mark-read/`;
const NOTIF_MARK_ALL_READ_URL = "notifications/mark-all-read/";
const NOTIF_POLL_INTERVAL_MS = 30000;

const NOTIF_LINK_MAP = {
  leave_request: "/leaves",
  regularization: "/attendance",
  task: "/task",
};

// ── Small "5m ago" / "2h ago" style relative-time formatter — no extra
// dependency needed for this. ──
const formatRelativeTime = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
};

// ── Per-type icon + accent, same visual language as the full Notifications
// page (getTypeIcon in notificationutils.js) so the dropdown preview feels
// like a natural extension of that page rather than a separate design. ──
const getNotifTypeMeta = (type) => {
  switch (type) {
    case 1:
    case 2:
    case 3:
    case 4:
      return { icon: <FaCalendarAlt />, cls: "leave" };
    case 5:
    case 6:
    case 7:
      return { icon: <FaCalendarAlt />, cls: "regularization" };
    case 9:
    case 10:
      return { icon: <FaTasks />, cls: "task" };
    case 11:
      return { icon: <FaBullhorn />, cls: "announcement" };
    default:
      return { icon: <FaInfoCircle />, cls: "system" };
  }
};

export default function Navbar({ onMenuClick }) {
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [showLogout, setShowLogout] = useState(false);
  const navigate = useNavigate();
  const profileRef = useRef(null);

  const { user, updateUser, hasPermission, hasAnyPermission } = useAuth();

  const currentTheme = user?.theme === "dark" || (user?.theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches) ? "dark" : "light";
  const isDark = currentTheme === "dark";

  // Prefer the employee record's photo (kept in sync with Employee
  // Add/Edit + View + Org Chart) and fall back to the user profile photo
  // so the navbar shows whichever one was actually set.
  const avatarUrl =
    user?.employee_details?.photo_url ||
    user?.employee_details?.photo ||
    user?.profile_photo_url ||
    null;

  const initials = (user?.full_name || "?").charAt(0).toUpperCase();

  // ── Notifications state ──
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const toggleTheme = async () => {
    const newTheme = isDark ? "light" : "dark";
    updateUser({ theme: newTheme }); // Optimistic update
    try {
      await updateProfile({ theme: newTheme });
    } catch {
      toast.error("Failed to save theme preference");
      updateUser({ theme: isDark ? "dark" : "light" }); // Revert
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        profileRef.current &&
        !profileRef.current.contains(event.target)
      ) {
        setOpen(false);
        setOpenMenu(null);
        setNotifOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ── Poll the unread count in the background so the badge stays fresh
  // even while the dropdown is closed. ──
  useEffect(() => {
    let cancelled = false;

    const fetchUnreadCount = () => {
      api
        .get(NOTIF_UNREAD_COUNT_URL)
        .then((res) => {
          if (cancelled) return;
          const count = res.data?.data?.unread_count ?? res.data?.unread_count ?? 0;
          setUnreadCount(count);
        })
        .catch(() => {});
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, NOTIF_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // ── Fetch the actual list only when the dropdown is opened, so we're
  // not pulling full notification bodies on every poll tick. ──
  useEffect(() => {
    if (!notifOpen) return;

    let cancelled = false;
    setNotifLoading(true);

    api
      .get(NOTIF_LIST_URL, { params: { page_size: 10 } })
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data || res.data?.results || res.data || [];
        setNotifications(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setNotifications([]);
      })
      .finally(() => {
        if (!cancelled) setNotifLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [notifOpen]);

  // Profile dropdown opens/closes purely on click now — no hover, on any
  // device (mouse, touch, or otherwise).
  const toggleProfile = () => {
    setOpenMenu(null);
    setNotifOpen(false);
    setOpen((prev) => !prev);
  };

  const toggleNotif = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    setOpenMenu(null);
    setNotifOpen((prev) => !prev);
  };

  const goToProfile = (tab) => {
    navigate(`/profile?tab=${tab}`);
    setOpen(false);
  };

  const handleLogoutClick = () => {
    setOpen(false);
    setShowLogout(true);
  };

  // Clicking a notification: mark it read (optimistic, best-effort on the
  // server) and send the user to wherever it points. Announcements (and
  // any other type not in NOTIF_LINK_MAP — e.g. a holiday/outing notice
  // with no specific module to jump to) previously had no `link`, so the
  // click did nothing. They now fall back to the Notifications module
  // itself so every notification is actionable.
  const handleNotificationClick = (notification) => {
    setNotifOpen(false);

    if (!notification.is_read) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
      api.post(NOTIF_MARK_READ_URL(notification.id)).catch(() => {});
    }

    const link = notification.link || NOTIF_LINK_MAP[notification.related_object_type] || "/notifications";
    navigate(link);
  };

  const handleMarkAllRead = (e) => {
    e.stopPropagation();
    if (unreadCount === 0) return;
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
    api.post(NOTIF_MARK_ALL_READ_URL).catch(() => {});
  };

  return (
    <div className="navbar" ref={profileRef}>
      <div className="navbar-left">
        {/* Mobile-only hamburger — opens the off-canvas sidebar drawer.
            Hidden above the phone breakpoint via CSS. */}
        <button
          className="mobile-menu-btn"
          onClick={onMenuClick}
          aria-label="Open menu"
        >
          <FaBars />
        </button>
        <Breadcrumbs />
      </div>

      <div className="navbar-right">
        {/* USERS MODULE DROPDOWN */}
        {hasAnyPermission(["user.view", "role.view", "permission.view"]) && (
          <div className="nav-dropdown-wrapper" >
            <button
              className={`nav-dropdown-btn ${openMenu === "users" ? "active" : ""}`}
              onClick={() => { setOpen(false); setNotifOpen(false); setOpenMenu(openMenu === "users" ? null : "users"); }}
            >
              <FaUserCog />
              <span>Users</span>
              <FaAngleDown className="nav-dropdown-caret" />
            </button>

            {openMenu === "users" && (
              <div className="nav-dropdown-menu">
                {hasPermission("user.view") && (
                  <div className="nav-dropdown-item" onClick={() => { navigate("/users"); setOpenMenu(null); }}>
                    <span className="nav-dropdown-icon"><FaUserCog /></span>
                    <span>Users</span>
                  </div>
                )}
                {hasAnyPermission(["role.view", "permission.view"]) && (
                  <div className="nav-dropdown-item" onClick={() => { navigate("/roles"); setOpenMenu(null); }}>
                    <span className="nav-dropdown-icon"><FaUserShield /></span>
                    <span>Roles & Permissions</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ORGANIZATION MODULE DROPDOWN */}
        {hasAnyPermission(["department.view", "designation.view"]) && (
          <div className="nav-dropdown-wrapper" >
            <button
              className={`nav-dropdown-btn ${openMenu === "org" ? "active" : ""}`}
              onClick={() => { setOpen(false); setNotifOpen(false); setOpenMenu(openMenu === "org" ? null : "org"); }}
            >
              <FaBuilding />
              <span>Organization</span>
              <FaAngleDown className="nav-dropdown-caret" />
            </button>

            {openMenu === "org" && (
              <div className="nav-dropdown-menu">
                {hasPermission("department.view") && (
                  <div className="nav-dropdown-item" onClick={() => { navigate("/department"); setOpenMenu(null); }}>
                    <span className="nav-dropdown-icon"><FaBuilding /></span>
                    <span>Departments</span>
                  </div>
                )}
                {hasPermission("designation.view") && (
                  <div className="nav-dropdown-item" onClick={() => { navigate("/designation"); setOpenMenu(null); }}>
                    <span className="nav-dropdown-icon"><FaIdBadge /></span>
                    <span>Designations</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* CALENDAR MODULE DROPDOWN */}
        {/* {hasAnyPermission(["shift.view", "holiday.view", "weekend_policy.view"]) && (
          <div className="nav-dropdown-wrapper">
            <button
              className={`nav-dropdown-btn ${openMenu === "calendar" ? "active" : ""}`}
              onClick={() => { setOpen(false); setOpenMenu(openMenu === "calendar" ? null : "calendar"); }}
            >
              <FaCalendarAlt />
              <span>Calendar</span>
              <FaAngleDown className="nav-dropdown-caret" />
            </button>

            {openMenu === "calendar" && (
              <div className="nav-dropdown-menu" style={{ minWidth: 190 }}>
                <div className="nav-dropdown-item" onClick={() => { navigate("/work-calendar"); setOpenMenu(null); }}>
                  <span className="nav-dropdown-icon"><FaCalendarAlt /></span>
                  <span>Work Calendar</span>
                </div>
              </div>
            )}
          </div>
        )} */}

        {/* NOTIFICATIONS */}
        <div className="notif-wrapper">
          <button
            type="button"
            className={`notif-bell-btn ${notifOpen ? "active" : ""} ${unreadCount > 0 ? "has-unread" : ""}`}
            onClick={toggleNotif}
            aria-label="Notifications"
            aria-expanded={notifOpen}
          >
            <FaBell />
            {unreadCount > 0 && (
              <span className="notif-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
            )}
          </button>

          {notifOpen && (
            <div className="notif-dropdown" onClick={(e) => e.stopPropagation()}>
              <div className="notif-dropdown-header">
                <span><FaBell /> Notifications</span>
                {unreadCount > 0 && (
                  <button type="button" className="notif-markall-btn" onClick={handleMarkAllRead}>
                    <FaCheckDouble /> Mark all read
                  </button>
                )}
              </div>

              <div className="notif-list">
                {notifLoading ? (
                  <div className="notif-empty">Loading…</div>
                ) : notifications.length === 0 ? (
                  <div className="notif-empty">
                    <FaBell className="notif-empty-icon" />
                    No notifications yet
                  </div>
                ) : (
                  notifications.map((n) => {
                    const meta = getNotifTypeMeta(n.notification_type);
                    return (
                      <div
                        key={n.id}
                        className={`notif-item${n.is_read ? "" : " unread"}`}
                        onClick={() => handleNotificationClick(n)}
                      >
                        <div className={`notif-item-icon ${meta.cls}`}>
                          {meta.icon}
                          {!n.is_read && <span className="notif-item-dot" />}
                        </div>
                        <div className="notif-item-body">
                          <div className="notif-item-title">{n.title}</div>
                          {n.message && <div className="notif-item-message">{n.message}</div>}
                          <div className="notif-item-time">{formatRelativeTime(n.created_at)}</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div
                className="notif-dropdown-footer"
                onClick={() => { setNotifOpen(false); navigate("/notifications"); }}
              >
                View all notifications <FaAngleRight />
              </div>
            </div>
          )}
        </div>

        {/* Theme Toggle */}
        <div
          className={`theme-toggle ${isDark ? "dark" : ""}`}
          onClick={toggleTheme}
        >
          <span className="toggle-icon sun"><FaSun /></span>
          <span className="toggle-icon moon"><FaMoon /></span>
          <span className="toggle-thumb" />
        </div>

        {/* Profile — opens on click only (no hover) */}
        <div className="profile">
          <div
            className="profile-info"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleProfile();
            }}
            role="button"
            tabIndex={0}
            aria-expanded={open}
            aria-label="Open profile menu"
          >
            <div className="profile-avatar">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={user.full_name}
                  className="profile-avatar-img"
                />
              ) : (
                <div className="avatar-letter nav-avatar-letter">
                  {initials}
                </div>
              )}
            </div>
            {user?.full_name && (
              <span style={{ marginLeft: 6, fontSize: "0.85rem", fontWeight: 500 }}>
                {user.full_name.split(" ")[0]}
              </span>
            )}
          </div>

          {open && (
            <div
              className="dropdown"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Premium profile header with large avatar */}
              {user && (
                <div className="dropdown-header">
                  <div className="dropdown-avatar">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt={user.full_name} />
                    ) : (
                      <div className="avatar-letter nav-avatar-letter-lg">
                        {initials}
                      </div>
                    )}
                  </div>
                  {user.full_name && (
                    <div className="dropdown-user-name">{user.full_name}</div>
                  )}
                  <div className="dropdown-user-designation">
                    {typeof user.employee_details?.designation === "object"
                      ? user.employee_details.designation?.name || "No Designation"
                      : user.employee_details?.designation || "No Designation"}
                  </div>
                  <div className="dropdown-user-email">{user.email}</div>
                </div>
              )}

              <div className="dropdown-divider" />

              <div className="dropdown-item" onClick={() => goToProfile("personal")}>
                <span className="dropdown-item-icon"><FaUserCog/></span>
                <span>Profile Settings</span>
              </div>
               {/* <div
                className="dropdown-item"
                onClick={() => goToProfile("bank")}
              >
                <span className="dropdown-item-icon"><FaUniversity /></span>
                <span>Bank Account</span>
              </div>
               <div
                className="dropdown-item"
                onClick={() => goToProfile("emergency")}
              >
                <span className="dropdown-item-icon"><FaPhoneAlt /></span>
                <span>Emergency Contact</span>
              </div> */}
              <div
                className="dropdown-item"
                onClick={() => goToProfile("documents")}
              >
                <span className="dropdown-item-icon"><FaFolderOpen /></span>
                <span>My Documents</span>
              </div>
              <div className="dropdown-item" onClick={() => goToProfile("security")}>
                <span className="dropdown-item-icon"><FaLock/></span>
                <span>Change Password</span>
              </div>
             

              <div className="dropdown-divider" />

              <div className="dropdown-item logout" onClick={handleLogoutClick}>
                <span className="dropdown-item-icon"><FaSignOutAlt/></span>
                <span>Logout</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {showLogout && <LogoutModal setShowLogout={setShowLogout} />}
    </div>
  );
}